import {
    Box,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    List,
    ListItem,
    Portal,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TbChevronDown } from 'react-icons/tb';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';
import { computeFixedMenuPos } from './InventoryTypeaheadField';

interface QuantityComboFieldProps {
    value: number | null;
    onChange: (value: number) => void;
    /** Inclusive minimum for typed/selected values (default 1). */
    min?: number;
    /** Inclusive maximum; dropdown lists every integer from min..max. */
    max: number;
    size?: 'sm' | 'md';
    isDisabled?: boolean;
}

function displayFromValue(v: number | null): string {
    if (v == null) return '';
    return String(v);
}

/**
 * Editable combo box: type a quantity or pick a preset from the drop-down
 * (same interaction model as Word’s font-size control).
 */
const QuantityComboField = ({
    value,
    onChange,
    min = 1,
    max,
    size = 'sm',
    isDisabled = false,
}: QuantityComboFieldProps) => {
    const [text, setText] = useState('');
    const [focused, setFocused] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{
        top?: number;
        bottom?: number;
        left: number;
        width: number;
        maxH: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const pickingFromMenuRef = useRef(false);

    const options = useMemo(() => {
        const upper = Math.max(min, max);
        const length = Math.max(0, upper - min + 1);
        return Array.from({ length }, (_, i) => min + i);
    }, [min, max]);

    useEffect(() => {
        if (!focused) {
            setText(displayFromValue(value));
        }
    }, [value, focused]);

    const clamp = (n: number) => Math.min(Math.max(n, min), max);

    const commit = (raw: string) => {
        const trimmed = raw.trim();
        if (trimmed === '') {
            onChange(clamp(value ?? min));
            return;
        }
        const n = parseInt(trimmed, 10);
        if (!Number.isNaN(n)) {
            onChange(clamp(n));
        }
    };

    const updateMenuPosition = () => {
        const el = inputRef.current;
        if (!el) return;
        setMenuPos(computeFixedMenuPos(el.getBoundingClientRect()));
    };

    useLayoutEffect(() => {
        if (!menuOpen) {
            setMenuPos(null);
            return;
        }
        updateMenuPosition();
        const onScrollOrResize = () => updateMenuPosition();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [menuOpen, options.length]);

    useEffect(() => {
        if (!menuOpen) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [menuOpen]);

    const displayValue = focused ? text : displayFromValue(value);

    return (
        <Box ref={containerRef} position="relative">
            <InputGroup size={size}>
                <Input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    placeholder={String(min)}
                    value={displayValue}
                    isDisabled={isDisabled}
                    onChange={(e) => {
                        const next = e.target.value.replace(/[^\d]/g, '');
                        setText(next);
                    }}
                    onFocus={() => {
                        setFocused(true);
                        setText(displayFromValue(value));
                        if (!isDisabled) setMenuOpen(true);
                    }}
                    onBlur={() => {
                        if (pickingFromMenuRef.current) {
                            pickingFromMenuRef.current = false;
                            setFocused(false);
                            return;
                        }
                        setFocused(false);
                        commit(text);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                            setMenuOpen(false);
                        } else if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setMenuOpen(true);
                        } else if (e.key === 'Escape') {
                            setMenuOpen(false);
                        }
                    }}
                    pr={8}
                    {...inventoryFieldProps}
                />
                <InputRightElement h="100%" width="2rem">
                    <IconButton
                        aria-label="Show quantity options"
                        aria-expanded={menuOpen}
                        icon={<Icon as={TbChevronDown as React.ElementType} boxSize={3.5} />}
                        size="xs"
                        variant="ghost"
                        h="70%"
                        minW="1.5rem"
                        isDisabled={isDisabled}
                        tabIndex={-1}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            if (isDisabled) return;
                            setMenuOpen((open) => !open);
                            inputRef.current?.focus();
                        }}
                    />
                </InputRightElement>
            </InputGroup>

            {menuOpen && menuPos && (
                <Portal>
                    <Box
                        ref={menuRef}
                        position="fixed"
                        zIndex={1500}
                        top={menuPos.top}
                        bottom={menuPos.bottom}
                        left={menuPos.left}
                        w={`${menuPos.width}px`}
                        maxH={`${menuPos.maxH}px`}
                        overflowY="auto"
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="lg"
                        boxShadow="none"
                        py={1}
                    >
                        <List spacing={0}>
                            {options.map((opt) => {
                                const selected = value === opt;
                                return (
                                    <ListItem
                                        key={opt}
                                        px={3}
                                        py={1.5}
                                        fontSize="sm"
                                        cursor="pointer"
                                        bg={selected ? 'gray.100' : 'transparent'}
                                        fontWeight={selected ? 'semibold' : 'normal'}
                                        _hover={{ bg: 'gray.50' }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            pickingFromMenuRef.current = true;
                                            onChange(opt);
                                            setText(String(opt));
                                            setFocused(false);
                                            setMenuOpen(false);
                                        }}
                                    >
                                        {opt}
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                </Portal>
            )}
        </Box>
    );
};

export default QuantityComboField;
